import dayjs from 'dayjs';
import 'dayjs/locale/en';
import 'dayjs/locale/ru';
import utc from 'dayjs/plugin/utc';
import { observer } from 'mobx-react-lite';
import { FC, RefObject } from 'react';
import {
  Calendar as CalendarComponent,
  dayjsLocalizer,
  EventProps,
  HeaderProps,
  NavigateAction,
  SlotInfo,
  ToolbarProps,
  View,
} from 'react-big-calendar';
import withDragAndDrop, {
  EventInteractionArgs,
  OnDragStartArgs,
} from 'react-big-calendar/lib/addons/dragAndDrop';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';
import 'react-big-calendar/lib/css/react-big-calendar.css';

import dateService from '@gem/services/date-service';

import { getLanguage } from 'UTILS/getLanguage';

import { STEP } from '../Content.constants';
import { IEventCalendar, IRange } from '../Content.types';
import { MonthDateHeader } from '../Header/MonthDateHeader/MonthDateHeader';
import { MonthHeader } from '../Header/MonthHeader/MonthHeader';
import { WeekHeader } from '../Header/WeekHeader/WeekHeader';
import { TIMES_SLOTS } from './DnDCalendar.constants';
import './DnDCalendar.css';
import { useDnDCalendarPresenter } from './DnDCalendar.presenter';

dayjs.extend(utc);
dayjs.locale(getLanguage());

const localizer = dayjsLocalizer(dayjs);

const DnDCalendarComponent = withDragAndDrop<IEventCalendar>(CalendarComponent);

interface IDnDCalendarProps {
  calRef: RefObject<HTMLDivElement>;
  events: IEventCalendar[];
  currentView: View;
  currentDate: Date;
  isWeekView: boolean;
  isDayView: boolean;
  scrollToTime: Date | undefined;
  changeView(view: View): void;
  onNavigate(date: Date, view: View, action: NavigateAction): void;
  onSelectEvent(selectedEvent: IEventCalendar): void;
  onSelectSlot(info: SlotInfo): void;
  onRangeChange(range: IRange): void;
  onDrillDown(date: Date): void;
  onDraggableAccessor(event: IEventCalendar): boolean;
  onDragStart(data: OnDragStartArgs<IEventCalendar>): void;
  onEventDrop(data: EventInteractionArgs<IEventCalendar>): Promise<void>;
  Toolbar: React.ComponentType<ToolbarProps<IEventCalendar>>;
  Event: React.ComponentType<EventProps<IEventCalendar>>;
  DayHeader: React.ComponentType<HeaderProps>;
}

export const DnDCalendar: FC<IDnDCalendarProps> = observer((props) => {
  const {
    events,
    currentView,
    currentDate,
    isWeekView,
    scrollToTime,
    changeView,
    onNavigate,
    onSelectEvent,
    onSelectSlot,
    onRangeChange,
    onDrillDown,
    onDraggableAccessor,
    onDragStart,
    onEventDrop,
    Toolbar,
    Event,
    DayHeader,
  } = props;
  const { presenterAllDayMaxRows, presenter } = useDnDCalendarPresenter(props);

  return (
    <DnDCalendarComponent
      scrollToTime={scrollToTime}
      allDayMaxRows={presenterAllDayMaxRows.allDayMaxRows}
      onShowMore={presenterAllDayMaxRows.onShowMore}
      events={events}
      views={presenter.views}
      view={currentView}
      date={currentDate}
      localizer={localizer}
      popup={isWeekView}
      resizable={false}
      step={STEP}
      timeslots={TIMES_SLOTS}
      culture={getLanguage()}
      startAccessor="start"
      endAccessor="end"
      dayLayoutAlgorithm="no-overlap"
      selectable="ignoreEvents"
      showMultiDayTimes
      onView={changeView}
      onNavigate={onNavigate}
      onSelectEvent={onSelectEvent}
      onSelectSlot={onSelectSlot}
      onRangeChange={onRangeChange}
      onDrillDown={onDrillDown}
      draggableAccessor={onDraggableAccessor}
      onDragStart={onDragStart}
      onEventDrop={onEventDrop}
      messages={{ showMore: (total) => `+${total}` }}
      formats={{
        selectRangeFormat: ({ start, end }) =>
          `${presenter.getTime(start)} - ${presenter.getTime(end)}`,
        timeGutterFormat: (date): string =>
          dateService.format(date, {
            hour: '2-digit',
            minute: '2-digit',
          }) as string,
      }}
      components={{
        event: Event,
        toolbar: Toolbar,
        day: { header: DayHeader },
        month: {
          header: MonthHeader,
          dateHeader: MonthDateHeader,
        },
        week: {
          header: WeekHeader,
        },
      }}
    />
  );
});
